import React from 'react';
import { useRecoilValue } from 'recoil';
import { LazyImageLoader } from '../product_detail/components/LazyImageLoader';
import { assetPathFnState, environmentContextState } from '../recoil/atoms';
import { isMobileState } from '../recoil/selectors';
import { SignedIn, SignedOut } from '@clerk/clerk-react';
import { Link } from '../root/components/Link';
import { isInDateRange } from '../discounts';
import { CardBody } from '../components/CardBody';
import { CardHeader } from '../components/CardHeader';
import { CardFooter } from '../components/CardFooter';
import { sizeImage } from '../util';
import { DesktopBenefitsTable } from '../profile/DesktopBenefitsTable';
import { MobileBenefitsTable } from '../profile/MobileBenefitsTable';
import styles from './LoyaltyApp.module.scss';

// eslint-disable-next-line max-lines-per-function
const LoyaltyApp: React.FC = () => {
  const isMobile = useRecoilValue(isMobileState);
  const assetPathFn = useRecoilValue(assetPathFnState);
  const environmentContext = useRecoilValue(environmentContextState);
  const isFlashSaleActive = isInDateRange(environmentContext.loyaltyFlashSaleStartISO, environmentContext.loyaltyFlashSaleStopISO);
  const creditPerDollar = isFlashSaleActive ? environmentContext.loyaltyFlashSaleMultiplier : 1;

  return (
    <main role="main" className={styles.pageWrapper}>
      <CardBody>
        <div className={styles.header}>
          <h1>Join the Hive</h1>
          <span>Earn credits and rewards by joining, purchasing, and sharing Honeylove.</span>
          <LoyaltyCTA />
        </div>
      </CardBody>
      <CardFooter />
      <CardHeader />
      <CardBody>
        <div className={styles.promoIcons}>
          <PromoBlock image="loyalty-icon-bird.png" title="Free Shipping" description="On all orders when you earn Honey Bee or Queen Bee status." />
          <PromoBlock
            image="loyalty-icon-cupcake.png"
            title="Birthday Discounts"
            description="We’ll send you a special discount each year to celebrate your day."
          />
          <PromoBlock image="loyalty-icon-sunrise.png" title="Early Access" description="Be the first to know about sales, offers, and new product releases." />
          <PromoBlock image="loyalty-icon-coins.png" title="Double Credits" description="Get double credit on all purchases when you become a Queen Bee." />
        </div>
      </CardBody>
      <CardFooter />
      <CardHeader />
      <CardBody>
        <div className={styles.pointsDetails}>
          {!isMobile && <h2>Earning credit is easy. You&apos;ll receive 25 just by signing up!</h2>}
          {isMobile && <h2>Earning credit is easy.</h2>}
          {isMobile && <span style={{ fontSize: '14px' }}>You&apos;ll receive 25 just by signing up!</span>}
          <div className={styles.pointsInfoBoxWrapper}>
            <div className={styles.pointsInfoBox}>
              <LazyImageLoader alt="icon" src={sizeImage(assetPathFn('loyalty-icon-signup.png'), '66x66')} />
              <div>
                <div>25 Credits</div>
                <div>Create an account</div>
              </div>
            </div>
            <div className={styles.pointsInfoBox}>
              <LazyImageLoader alt="icon" src={sizeImage(assetPathFn('loyalty-icon-dollar.png'), '66x66')} />
              <div>
                {isFlashSaleActive ? (
                  <>
                    <div style={{ textDecoration: 'line-through' }}>1 Credit</div>
                    <div
                      style={{
                        position: 'absolute',
                        width: 'calc(100% + 8px)',
                        left: '-4px',
                        top: '-12px',
                        background: 'rgb(27, 127, 113, 0.9)',
                        color: 'white',
                        textAlign: 'center',
                      }}
                    >
                      <div
                        style={{
                          width: '0',
                          position: 'absolute',
                          bottom: '-4px',
                          left: '-5px',
                          height: '0',
                          borderRight: '5px solid #145c51',
                          borderLeft: '5px solid transparent',
                          borderBottom: '5px solid transparent',
                        }}
                      />
                      <div
                        style={{
                          width: '0',
                          position: 'absolute',
                          bottom: '-4px',
                          right: '-5px',
                          height: '0',
                          borderLeft: '5px solid #145c51',
                          borderRight: '5px solid transparent',
                          borderBottom: '5px solid transparent',
                        }}
                      />
                      Limited time! {creditPerDollar} credits.
                    </div>
                  </>
                ) : (
                  <div>1 Credit</div>
                )}
                <div>Every dollar spent</div>
              </div>
            </div>
            <div className={styles.pointsInfoBox}>
              <LazyImageLoader alt="icon" src={sizeImage(assetPathFn('loyalty-icon-past-orders.png'), '66x66')} />
              <div>
                <div>Bonus Credits</div>
                <div>For one year of order history</div>
              </div>
            </div>
          </div>
        </div>
      </CardBody>
      <CardBody>
        <div className={styles.tierWrapper}>
          <div className={styles.tierTableHeader}>
            <h2>100 credits = $10</h2>
            <div>Every 100 credits you earn = $10 to spend at Honeylove</div>
          </div>
          {!isMobile && <DesktopBenefitsTable />}
          {isMobile && <MobileBenefitsTable />}
          <div className={styles.asterisks}>
            <span>* Minimum annual spend of $200</span>
            <span>** Minimum annual spend of $450</span>
          </div>
          <LoyaltyCTA />
          <div className={styles.termsAndConditions}>
            <div>
              <a href="/pages/honeylove-hive-terms">click here</a> for the full terms &amp; conditions
            </div>
            <div>
              Honeylove Hive is for U.S. customers only. By creating an account, you affirm that you have read, understood, and consent to the&nbsp;
              <Link dataTrackLabel="Privacy Policy" dataTrackLocation="footer" href="/pages/privacy-policy" buttonType="ghost">
                privacy policy
              </Link>
              ,&nbsp;
              <Link dataTrackLabel="Terms of Service" dataTrackLocation="footer" href="/pages/terms-of-service" buttonType="ghost">
                terms of service
              </Link>
              &nbsp;, and the&nbsp;
              <Link href="/pages/honeylove-hive-terms" buttonType="ghost">
                Honeylove Hive Terms &amp; conditions
              </Link>
              .
            </div>
          </div>
        </div>
      </CardBody>
      <CardFooter />
    </main>
  );
};

const PromoBlock: React.FC<{ image: string; title: string; description: string }> = ({ image, title, description }) => {
  const assetPathFn = useRecoilValue(assetPathFnState);
  return (
    <div className={styles.promoIcon}>
      <div className={styles.promoIconImage}>
        <LazyImageLoader style={{ maxWidth: '100%' }} alt={title} src={sizeImage(assetPathFn(image), '200x200')} />
      </div>
      <div>
        <h3>{title}</h3>
        <span>{description}</span>
      </div>
    </div>
  );
};

const LoyaltyCTA: React.FC = () => {
  const clerkSignUpURL = `/pages/sign-up`;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'center', marginTop: '50px' }}>
      <SignedIn>
        <Link href="/pages/profile" dataTrackLabel="View your profile" dataTrackLocation="Loyalty detail page" size="medium" text="View my rewards" />
      </SignedIn>
      <SignedOut>
        <Link
          href={clerkSignUpURL}
          dataTrackLabel="Join loyalty program"
          dataTrackLocation="Loyalty detail page"
          buttonType="secondary"
          size="large"
          text="Join for free"
        />
        <a style={{ marginTop: 20 }} href="/pages/sign-in">
          Already a member? Sign In
        </a>
      </SignedOut>
    </div>
  );
};

export default LoyaltyApp;
